import { useQuery } from '@tanstack/react-query'
import { QUERY_KEY as TEXT_QUERY_KEY, queryPricing } from 'client/queries/queryPricing'
import { useBatchPricing } from 'client/hooks/useBatchPricing'
import { memo } from 'react'
import { getPricingPresentationType } from '~/client/utils/getPricingPresentationType'
import { PricePresentationType } from '@vp/price-platform'
import { PricePresentationWrapper } from '../PricePresentationWrapper'
import { ContextualPricePlatformPropTypes } from './interface'
import { usePricingContext } from 'client/contexts/pricing/context'

export const ContextualBatchPricePlatform = memo((props: ContextualPricePlatformPropTypes) => {
  const {
    productOptionsByProductKey,
    productOptionsHash: productOptionsHashProp,
    hideVatMessage,
    hideShippingMessageInVatMessage,
    pricingPresentationType,
    productKey,
    productVersion,
    differentialPrice,
    fullProductOptions,
    locale,
    market,
    quantity,
    pageId,
  } = props

  const pricingContext = usePricingContext()
  const retrievedPricingContext = pricingContext?.getPricingContext()

  const resolvedPricingPresentation =
    differentialPrice && pricingPresentationType ? getPricingPresentationType(pricingPresentationType) : pricingPresentationType

  const { differentialPriceData, productQuantity } = useBatchPricing({
    productOptionsByProductKey,
    productKey,
    productVersion,
    differentialPrice,
    fullProductOptions,
    locale,
    market,
    quantity,
    pricingContext,
    retrievedPricingContext,
  })

  const enabled = !!differentialPriceData && !!productKey && !!productVersion && !!productQuantity

  const {
    data: formattedPrices,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      TEXT_QUERY_KEY,
      {
        pricingResponse: differentialPriceData || undefined,
        quantity: productQuantity as number,
        locale,
        vatInc: retrievedPricingContext?.vatInclusive || false,
        hideVatMessage: hideVatMessage || differentialPrice, // Always hide VAT in differential
        pricingPresentationType: resolvedPricingPresentation || '',
        hideShippingMessageInVatMessage: hideShippingMessageInVatMessage || false,
        productOptionsHash: productOptionsHashProp,
      },
    ],
    queryFn: queryPricing,
    enabled,
  })
  const shouldShowLoadingState = isLoading && !isError

  return (
    <PricePresentationWrapper
      formattedPrices={formattedPrices}
      isLoading={shouldShowLoadingState}
      pageId={pageId}
      presentationType={resolvedPricingPresentation as PricePresentationType}
    />
  )
})

ContextualBatchPricePlatform.displayName = 'ContextualBatchPricePlatform'
